import 'maplibre-gl/dist/maplibre-gl.css';

import { Star } from 'lucide-react';
import {
    type GeoJSONSource,
    type LngLatBounds,
    type Map as MaplibreMap,
    type MapLayerMouseEvent,
    type Marker,
    type MarkerOptions,
    type Popup,
    type PopupOptions,
} from 'maplibre-gl';
import {
    Fragment,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { StatusBadge } from '@apps/web/components/ui/status_badge';
import { type CoverageMapClients } from '@apps/web/features/coverage/api/coverage_queries';
import { useCoverageOpenShiftClients } from '@apps/web/features/coverage/api/open_shift_clients';
import { useCoverageShiftAvailability } from '@apps/web/features/coverage/api/shift_availability';
import {
    AVAILABILITY_MAP_STYLES,
    CLIENT_MARKER_COLOR,
    OPEN_SHIFT_CLIENT_MARKER_COLOR,
} from '@apps/web/features/coverage/model/map_colors';
import {
    nearbyCaregivers,
    radiusCirclePolygon,
} from '@apps/web/features/coverage/model/nearby_caregivers';
import { type CoverageMapStatus } from '@apps/web/features/coverage/model/use_coverage_map';
import { CoverageMapSearch } from '@apps/web/features/coverage/ui/coverage_map_search';
import {
    clientStarImage,
    potentialClientPinElement,
} from '@apps/web/features/coverage/ui/map_markers';
import {
    MapPointPopup,
    type SelectedMapPoint,
} from '@apps/web/features/coverage/ui/map_point_popup';
import {
    MapSelectionCard,
    type PotentialClientPin,
} from '@apps/web/features/coverage/ui/map_selection_card';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { configureMaplibreWorker, MAP_STYLE } from '@apps/web/lib/map_style';
import { useMode } from '@apps/web/lib/mode_store';
import {
    type CalendarShiftListResponse,
    type WorkforceMapClientPoint,
    type WorkforceMapPoint,
} from '@libraries/typescript/api_client';

type MaplibreConstructors = {
    readonly LngLatBounds: new () => LngLatBounds;
    readonly Marker: new (options?: MarkerOptions) => Marker;
    readonly Popup: new (options?: PopupOptions) => Popup;
};

const CLIENTS_LAYER_ID = 'coverage-clients';
const STAR_IMAGE_ID = 'coverage-client-star';
const OPEN_SHIFT_STAR_IMAGE_ID = 'coverage-client-star-open-shift';
const MUTED_STAR_IMAGE_ID = 'coverage-client-star-muted';
const EMPTY_CLIENTS: ReadonlyArray<WorkforceMapClientPoint> = [];

const POINTS_SOURCE_ID = 'coverage-caregivers';
const RADIUS_SOURCE_ID = 'coverage-radius';

const SELECTED_SHIFT_MAP_STYLES = {
    available: {
        ...AVAILABILITY_MAP_STYLES.available,
        label: 'Available for selected shifts',
    },
    unavailable: {
        ...AVAILABILITY_MAP_STYLES.unavailable,
        label: 'Unavailable for selected shifts',
    },
    scheduled: {
        ...AVAILABILITY_MAP_STYLES.scheduled,
        label: 'Scheduled during selected shifts',
    },
    unknown: AVAILABILITY_MAP_STYLES.unknown,
};

export function CoverageMapPane({
    renderToolbar,
    clearEpoch = 0,
    caregiverSearch = '',
    onCaregiverSearch,
    status,
    errorMessage,
    refreshErrorMessage,
    onRetry,
    points,
    clientsLayerEnabled,
    clients,
    organizationId,
    visibleCaregiverIds,
}: {
    readonly clearEpoch?: number;
    readonly caregiverSearch?: string;
    readonly renderToolbar?: (
        searchControl?: ReactNode,
        mapControls?: ReactNode,
        onRefresh?: () => void,
    ) => ReactNode;
    readonly onCaregiverSearch?: (query: string) => void;
    readonly status: CoverageMapStatus;
    readonly errorMessage: string | null;
    readonly refreshErrorMessage: string | null;
    readonly onRetry: () => void;
    readonly points: ReadonlyArray<WorkforceMapPoint>;
    readonly clientsLayerEnabled: boolean;
    readonly clients: CoverageMapClients;
    readonly organizationId: string | null;
    readonly visibleCaregiverIds: ReadonlySet<string>;
}) {
    const { formatName } = useFormatPersonName();
    const mode = useMode();
    const [showClients, setShowClients] = useState(true);
    const [onlyOpenShiftClients, setOnlyOpenShiftClients] = useState(false);
    const clientsVisible =
        clientsLayerEnabled && (showClients || onlyOpenShiftClients);
    const openShiftClients = useCoverageOpenShiftClients(
        organizationId,
        clientsVisible &&
            (onlyOpenShiftClients || Boolean(clients.data?.points.length)),
    );
    const openShiftClientIds = useMemo(
        () => new Set(openShiftClients.clients.map((client) => client.id)),
        [openShiftClients.clients],
    );
    const permittedClientPoints =
        clientsLayerEnabled && !clients.error
            ? (clients.data?.points ?? EMPTY_CLIENTS)
            : EMPTY_CLIENTS;
    const clientPoints = useMemo(
        () =>
            clientsVisible
                ? onlyOpenShiftClients
                    ? permittedClientPoints.filter((point) =>
                          openShiftClientIds.has(point.client_id),
                      )
                    : permittedClientPoints
                : EMPTY_CLIENTS,
        [
            clientsVisible,
            onlyOpenShiftClients,
            permittedClientPoints,
            openShiftClientIds,
        ],
    );
    const containerRef = useRef<HTMLDivElement | null>(null);
    const pointPopupRef = useRef<Popup | null>(null);
    const [selectedPoint, setSelectedPoint] = useState<SelectedMapPoint | null>(
        null,
    );
    const pinnedPointRef = useRef(false);
    const selectMapPoint = useCallback((point: SelectedMapPoint | null) => {
        pinnedPointRef.current = point !== null;
        pointPopupRef.current?.remove();
        pointPopupRef.current = null;
        setSelectedPoint(point);
    }, []);
    const closeMapPoint = useCallback(
        () => selectMapPoint(null),
        [selectMapPoint],
    );
    const mapRef = useRef<MaplibreMap | null>(null);
    const fittedLocationsRef = useRef<string | null>(null);
    const maplibreConstructorsRef = useRef<MaplibreConstructors | null>(null);
    const [isMapReady, setIsMapReady] = useState(false);
    const [isMapFullscreen, setIsMapFullscreen] = useState(false);
    const [hasRendererError, setHasRendererError] = useState(false);
    const [rendererAttempt, setRendererAttempt] = useState(0);
    const [potentialClient, setPotentialClient] =
        useState<PotentialClientPin | null>(null);
    const [selectedShifts, setSelectedShifts] = useState<
        CalendarShiftListResponse['shifts']
    >([]);
    const [selectedOutreachIds, setSelectedOutreachIds] = useState<
        ReadonlySet<string>
    >(new Set());
    const updateOutreachSelection = useCallback(
        (ids: ReadonlyArray<string>) => {
            setSelectedOutreachIds((previous) =>
                previous.size === ids.length &&
                ids.every((id) => previous.has(id))
                    ? previous
                    : new Set(ids),
            );
        },
        [],
    );
    const [radiusMiles, setRadiusMiles] = useState(5);
    const [searchClearEpoch, setSearchClearEpoch] = useState(0);
    const [focusedCaregiver, setFocusedCaregiver] =
        useState<WorkforceMapPoint | null>(null);
    const lastCaregiverSearchRef = useRef(caregiverSearch);
    const visiblePoints = useMemo(
        () =>
            points.filter((point) =>
                visibleCaregiverIds.has(point.caregiver_id),
            ),
        [points, visibleCaregiverIds],
    );
    const nearby = useMemo(
        () =>
            potentialClient
                ? nearbyCaregivers(visiblePoints, potentialClient, radiusMiles)
                : [],
        [visiblePoints, potentialClient, radiusMiles],
    );
    const mappedPoints = useMemo(() => {
        if (!potentialClient) {
            return visiblePoints;
        }
        const nearbyIds = new Set(
            nearby.map(({ point }) => point.caregiver_id),
        );
        return points.filter(
            (point) =>
                nearbyIds.has(point.caregiver_id) ||
                selectedOutreachIds.has(point.caregiver_id),
        );
    }, [potentialClient, nearby, visiblePoints, points, selectedOutreachIds]);
    const activeShiftIds = potentialClient?.clientId
        ? selectedShifts.map((shift) => shift.id)
        : [];
    const shiftAvailability = useCoverageShiftAvailability(
        organizationId,
        activeShiftIds,
        mappedPoints.map((point) => point.caregiver_id),
    );
    const hasSelectedShifts = activeShiftIds.length > 0;
    const availabilityStyles = hasSelectedShifts
        ? SELECTED_SHIFT_MAP_STYLES
        : AVAILABILITY_MAP_STYLES;
    const availabilityByCaregiver = useMemo(
        () =>
            new Map(
                mappedPoints.map(
                    (point) =>
                        [
                            point.caregiver_id,
                            hasSelectedShifts
                                ? shiftAvailability.isLoading ||
                                  shiftAvailability.isValidating ||
                                  shiftAvailability.error
                                    ? 'unknown'
                                    : (shiftAvailability.statuses.get(
                                          point.caregiver_id,
                                      ) ?? 'unknown')
                                : (point.availability_status ?? 'unknown'),
                        ] as const,
                ),
            ),
        [
            mappedPoints,
            hasSelectedShifts,
            shiftAvailability.isLoading,
            shiftAvailability.isValidating,
            shiftAvailability.error,
            shiftAvailability.statuses,
        ],
    );
    const visibleLocationsKey = JSON.stringify([
        Boolean(caregiverSearch),
        visiblePoints.map((point) => [
            point.caregiver_id,
            point.longitude,
            point.latitude,
        ]),
    ]);
    useEffect(() => {
        if (caregiverSearch === lastCaregiverSearchRef.current) {
            return;
        }
        lastCaregiverSearchRef.current = caregiverSearch;
        setPotentialClient(null);
        setFocusedCaregiver(null);
    }, [caregiverSearch]);
    const changeCaregiverSearch = useCallback(
        (query: string) => {
            lastCaregiverSearchRef.current = query;
            onCaregiverSearch?.(query);
        },
        [onCaregiverSearch],
    );
    const colorByCaregiver = useMemo(
        () =>
            new Map(
                mappedPoints.map((point) => [
                    point.caregiver_id,
                    hasSelectedShifts &&
                    selectedOutreachIds.has(point.caregiver_id)
                        ? CLIENT_MARKER_COLOR
                        : AVAILABILITY_MAP_STYLES[
                              availabilityByCaregiver.get(point.caregiver_id) ??
                                  'unknown'
                          ].color,
                ]),
            ),
        [
            mappedPoints,
            availabilityByCaregiver,
            hasSelectedShifts,
            selectedOutreachIds,
        ],
    );
    useEffect(() => {
        setPotentialClient(null);
        setFocusedCaregiver(null);
        setOnlyOpenShiftClients(false);
        updateOutreachSelection([]);
        closeMapPoint();
    }, [
        organizationId,
        mode,
        clearEpoch,
        closeMapPoint,
        updateOutreachSelection,
    ]);

    useEffect(() => {
        if (
            selectedPoint &&
            !(selectedPoint.kind === 'client'
                ? clientPoints.some(
                      (point) => point.client_id === selectedPoint.id,
                  ) ||
                  (potentialClient?.clientId === selectedPoint.id &&
                      permittedClientPoints.some(
                          (point) => point.client_id === selectedPoint.id,
                      ))
                : mappedPoints.some(
                      (point) => point.caregiver_id === selectedPoint.id,
                  ))
        ) {
            closeMapPoint();
        }
    }, [
        selectedPoint,
        clientPoints,
        mappedPoints,
        potentialClient?.clientId,
        permittedClientPoints,
        closeMapPoint,
    ]);

    useEffect(() => {
        setPotentialClient((current) =>
            current?.clientId &&
            !permittedClientPoints.some(
                (point) => point.client_id === current.clientId,
            )
                ? null
                : current,
        );
    }, [permittedClientPoints]);

    useEffect(() => {
        if (status !== 'ready' || !containerRef.current) {
            return;
        }
        let cancelled = false;
        let failed = false;
        let map: MaplibreMap | null = null;
        let cleanupFullscreen: (() => void) | undefined;
        const container = containerRef.current;
        fittedLocationsRef.current = null;
        setIsMapReady(false);
        setIsMapFullscreen(false);
        setHasRendererError(false);

        const fail = () => {
            if (cancelled || failed) {
                return;
            }
            failed = true;
            cleanupFullscreen?.();
            setIsMapFullscreen(false);
            map?.remove();
            map = null;
            mapRef.current = null;
            maplibreConstructorsRef.current = null;
            setIsMapReady(false);
            setHasRendererError(true);
        };

        import('maplibre-gl')
            .then((module) => {
                if (cancelled || !container.isConnected) {
                    return;
                }
                configureMaplibreWorker(module);
                maplibreConstructorsRef.current = {
                    LngLatBounds: module.LngLatBounds,
                    Marker: module.Marker,
                    Popup: module.Popup,
                };
                const createdMap = new module.Map({
                    attributionControl: { compact: false },
                    container,
                    style: MAP_STYLE,
                    center: [-98.5, 39.8],
                    zoom: 3,
                });
                map = createdMap;
                mapRef.current = createdMap;
                // Keep body-portaled menus and profile/outreach dialogs inside fullscreen.
                const fullscreen = new module.FullscreenControl({
                    container: document.body,
                });
                let ownsFullscreen = false;
                const enterFullscreen = () => {
                    ownsFullscreen = true;
                    setIsMapFullscreen(true);
                };
                const exitFullscreen = () => {
                    ownsFullscreen = false;
                    setIsMapFullscreen(false);
                };
                fullscreen.on('fullscreenstart', enterFullscreen);
                fullscreen.on('fullscreenend', exitFullscreen);
                cleanupFullscreen = () => {
                    fullscreen.off('fullscreenstart', enterFullscreen);
                    fullscreen.off('fullscreenend', exitFullscreen);
                    if (ownsFullscreen) {
                        ownsFullscreen = false;
                        document.body.classList.remove(
                            'maplibregl-pseudo-fullscreen',
                        );
                        if (document.fullscreenElement === document.body) {
                            void document.exitFullscreen();
                        }
                    }
                };
                createdMap.addControl(fullscreen, 'top-right');
                createdMap.addControl(
                    new module.NavigationControl({ showCompass: false }),
                    'top-right',
                );
                createdMap.on('load', () => {
                    if (!cancelled && !failed) {
                        setIsMapReady(true);
                    }
                });
                createdMap.on('error', () => {
                    if (!createdMap.isStyleLoaded()) {
                        fail();
                    }
                });
            })
            .catch(fail);

        return () => {
            cancelled = true;
            cleanupFullscreen?.();
            map?.remove();
            mapRef.current = null;
            maplibreConstructorsRef.current = null;
        };
    }, [rendererAttempt, status]);

    useEffect(() => {
        const map = mapRef.current;
        const constructors = maplibreConstructorsRef.current;
        if (!map || !constructors || !isMapReady) {
            return;
        }
        pointPopupRef.current?.remove();
        pointPopupRef.current = null;
        const featureCollection = {
            type: 'FeatureCollection' as const,
            features: [
                ...mappedPoints.map((point) => ({
                    type: 'Feature' as const,
                    geometry: {
                        type: 'Point' as const,
                        coordinates: [point.longitude, point.latitude],
                    },
                    properties: {
                        kind: 'caregiver',
                        caregiverId: point.caregiver_id,
                        name: point.name,
                        description: [
                            point.city,
                            point.postal_code,
                            point.office_name,
                            hasSelectedShifts && shiftAvailability.error
                                ? 'Availability not checked'
                                : hasSelectedShifts &&
                                    (shiftAvailability.isLoading ||
                                        shiftAvailability.isValidating)
                                  ? 'Checking availability…'
                                  : availabilityStyles[
                                        availabilityByCaregiver.get(
                                            point.caregiver_id,
                                        ) ?? 'unknown'
                                    ].label,
                        ]
                            .filter(Boolean)
                            .join(' · '),
                        color: colorByCaregiver.get(point.caregiver_id),
                    },
                })),
                ...clientPoints.map((point) => ({
                    type: 'Feature' as const,
                    geometry: {
                        type: 'Point' as const,
                        coordinates: [point.longitude, point.latitude],
                    },
                    properties: {
                        kind: 'client',
                        clientId: point.client_id,
                        name: point.name,
                        description: openShiftClientIds.has(point.client_id)
                            ? 'Client with open shifts in the next 30 days'
                            : 'Client',
                        markerImage: openShiftClientIds.has(point.client_id)
                            ? OPEN_SHIFT_STAR_IMAGE_ID
                            : potentialClient?.clientId &&
                                point.client_id !== potentialClient.clientId
                              ? MUTED_STAR_IMAGE_ID
                              : STAR_IMAGE_ID,
                    },
                })),
            ],
        };
        const existing = map.getSource(POINTS_SOURCE_ID);
        if (existing && 'setData' in existing) {
            (existing as GeoJSONSource).setData(featureCollection);
        } else {
            const showPointPopup = (event: MapLayerMouseEvent) => {
                if (pinnedPointRef.current) {
                    return;
                }
                const feature = event.features?.[0];
                if (!feature || feature.geometry.type !== 'Point') {
                    return;
                }
                const [longitude, latitude] = feature.geometry.coordinates;
                const content = document.createElement('div');
                const nameLine = document.createElement('strong');
                nameLine.textContent = formatName(
                    String(feature.properties?.['name'] ?? ''),
                );
                const descriptionLine = document.createElement('div');
                descriptionLine.textContent = String(
                    feature.properties?.['description'] ?? '',
                );
                content.append(nameLine, descriptionLine);
                pointPopupRef.current?.remove();
                pointPopupRef.current = new constructors.Popup({
                    closeButton: false,
                })
                    .setLngLat([longitude ?? 0, latitude ?? 0])
                    .setDOMContent(content)
                    .addTo(map);
            };
            map.addSource(POINTS_SOURCE_ID, {
                type: 'geojson',
                data: featureCollection,
            });
            map.addLayer({
                id: POINTS_SOURCE_ID,
                type: 'circle',
                source: POINTS_SOURCE_ID,
                filter: ['==', ['get', 'kind'], 'caregiver'],
                paint: {
                    'circle-radius': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        4,
                        4,
                        9,
                        6,
                        12,
                        8,
                        15,
                        10,
                    ],
                    'circle-color': ['get', 'color'],
                },
            });
            map.on('mousemove', CLIENTS_LAYER_ID, showPointPopup);
            map.on('mouseenter', CLIENTS_LAYER_ID, () => {
                map.getCanvas().style.cursor = 'pointer';
            });
            map.on('mouseleave', CLIENTS_LAYER_ID, () => {
                pointPopupRef.current?.remove();
                pointPopupRef.current = null;
                map.getCanvas().style.cursor = '';
            });
            map.on('mousemove', POINTS_SOURCE_ID, showPointPopup);
            map.on('click', POINTS_SOURCE_ID, (event: MapLayerMouseEvent) => {
                const feature = event.features?.[0];
                const id = feature?.properties?.['caregiverId'];
                if (
                    feature?.geometry.type !== 'Point' ||
                    typeof id !== 'string'
                ) {
                    return;
                }
                selectMapPoint({
                    id,
                    kind: 'caregiver',
                    name: String(feature.properties?.['name'] ?? ''),
                    longitude: feature.geometry.coordinates[0] ?? 0,
                    latitude: feature.geometry.coordinates[1] ?? 0,
                });
            });
            map.on('mouseenter', POINTS_SOURCE_ID, () => {
                map.getCanvas().style.cursor = 'pointer';
            });
            map.on('mouseleave', POINTS_SOURCE_ID, () => {
                pointPopupRef.current?.remove();
                pointPopupRef.current = null;
                map.getCanvas().style.cursor = '';
            });
        }

        if (clientPoints.length > 0 && !map.hasImage(STAR_IMAGE_ID)) {
            map.addImage(STAR_IMAGE_ID, clientStarImage(), { pixelRatio: 2 });
            map.addImage(
                OPEN_SHIFT_STAR_IMAGE_ID,
                clientStarImage(OPEN_SHIFT_CLIENT_MARKER_COLOR),
                { pixelRatio: 2 },
            );
            map.addImage(
                MUTED_STAR_IMAGE_ID,
                clientStarImage(AVAILABILITY_MAP_STYLES.unknown.color),
                {
                    pixelRatio: 2,
                },
            );
            map.addLayer({
                id: CLIENTS_LAYER_ID,
                type: 'symbol',
                source: POINTS_SOURCE_ID,
                filter: ['==', ['get', 'kind'], 'client'],
                layout: {
                    'icon-image': ['get', 'markerImage'],
                    'icon-size': [
                        'interpolate',
                        ['linear'],
                        ['zoom'],
                        4,
                        0.6,
                        10,
                        0.8,
                        14,
                        1,
                    ],
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                },
            });
        }
        // Refit only when caregiver locations change. Toggling client stars
        // or a late client response must not discard the user's pan and zoom.
        const fitPoints = caregiverSearch
            ? visiblePoints
            : [...visiblePoints, ...clientPoints];
        if (
            !potentialClient &&
            fitPoints.length > 0 &&
            fittedLocationsRef.current !== visibleLocationsKey
        ) {
            fittedLocationsRef.current = visibleLocationsKey;
            const bounds = new constructors.LngLatBounds();
            for (const point of fitPoints) {
                bounds.extend([point.longitude, point.latitude]);
            }
            map.fitBounds(bounds, { maxZoom: 10, padding: 48 });
        }
    }, [
        availabilityByCaregiver,
        availabilityStyles,
        caregiverSearch,
        clientPoints,
        colorByCaregiver,
        formatName,
        hasSelectedShifts,
        isMapReady,
        mappedPoints,
        openShiftClientIds,
        potentialClient,
        shiftAvailability.error,
        shiftAvailability.isLoading,
        shiftAvailability.isValidating,
        visibleLocationsKey,
        visiblePoints,
        selectMapPoint,
    ]);

    useEffect(() => {
        const map = mapRef.current;
        const constructors = maplibreConstructorsRef.current;
        if (!map || !constructors || !isMapReady || !potentialClient) {
            return;
        }
        const element = potentialClientPinElement(
            potentialClient.clientId &&
                openShiftClientIds.has(potentialClient.clientId)
                ? OPEN_SHIFT_CLIENT_MARKER_COLOR
                : CLIENT_MARKER_COLOR,
        );
        element.addEventListener('click', () => {
            if (potentialClient.clientId) {
                selectMapPoint({
                    id: potentialClient.clientId,
                    kind: 'client',
                    name: potentialClient.clientName ?? '',
                    latitude: potentialClient.latitude,
                    longitude: potentialClient.longitude,
                });
            }
        });
        const marker = new constructors.Marker({ anchor: 'center', element })
            .setLngLat([potentialClient.longitude, potentialClient.latitude])
            .addTo(map);
        return () => {
            marker.remove();
        };
    }, [potentialClient, isMapReady, openShiftClientIds, selectMapPoint]);

    useEffect(() => {
        const map = mapRef.current;
        const constructors = maplibreConstructorsRef.current;
        if (!map || !constructors || !isMapReady) {
            return;
        }
        const polygon = potentialClient
            ? radiusCirclePolygon(potentialClient, radiusMiles)
            : null;
        const data = {
            type: 'FeatureCollection' as const,
            features: polygon
                ? [
                      {
                          type: 'Feature' as const,
                          geometry: polygon,
                          properties: {},
                      },
                  ]
                : [],
        };
        const existing = map.getSource(RADIUS_SOURCE_ID);
        if (existing && 'setData' in existing) {
            (existing as GeoJSONSource).setData(data);
        } else {
            map.addSource(RADIUS_SOURCE_ID, { type: 'geojson', data });
            map.addLayer(
                {
                    id: 'coverage-radius-fill',
                    type: 'fill',
                    source: RADIUS_SOURCE_ID,
                    paint: {
                        'fill-color': CLIENT_MARKER_COLOR,
                        'fill-opacity': 0.08,
                    },
                },
                POINTS_SOURCE_ID,
            );
            map.addLayer(
                {
                    id: 'coverage-radius-line',
                    type: 'line',
                    source: RADIUS_SOURCE_ID,
                    paint: {
                        'line-color': CLIENT_MARKER_COLOR,
                        'line-opacity': 0.5,
                        'line-width': 1,
                    },
                },
                POINTS_SOURCE_ID,
            );
        }
        if (polygon) {
            const bounds = new constructors.LngLatBounds();
            for (const position of polygon.coordinates[0] ?? []) {
                bounds.extend(position);
            }
            map.fitBounds(bounds, {
                maxZoom: 13,
                padding:
                    (containerRef.current?.clientWidth ?? 0) < 640
                        ? 40
                        : { top: 50, bottom: 50, left: 360, right: 50 },
            });
        }
    }, [potentialClient, radiusMiles, isMapReady]);

    const plotPotentialClient = useCallback(
        (pin: PotentialClientPin) => {
            closeMapPoint();
            pointPopupRef.current?.remove();
            pointPopupRef.current = null;
            setFocusedCaregiver(null);
            setPotentialClient(pin);
            setSelectedShifts([]);
            updateOutreachSelection([]);
            changeCaregiverSearch('');
        },
        [changeCaregiverSearch, closeMapPoint, updateOutreachSelection],
    );

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapReady || mode !== 'live') {
            return;
        }
        const selectClient = (event: MapLayerMouseEvent) => {
            const clientId = event.features?.[0]?.properties?.['clientId'];
            const client = clientPoints.find(
                (point) => point.client_id === clientId,
            );
            if (client) {
                if (potentialClient?.clientId !== client.client_id) {
                    plotPotentialClient({
                        clientId: client.client_id,
                        clientName: client.name,
                        latitude: client.latitude,
                        longitude: client.longitude,
                    });
                }
                selectMapPoint({
                    id: client.client_id,
                    kind: 'client',
                    name: client.name,
                    latitude: client.latitude,
                    longitude: client.longitude,
                });
            }
        };
        map.on('click', CLIENTS_LAYER_ID, selectClient);
        return () => {
            map.off('click', CLIENTS_LAYER_ID, selectClient);
        };
    }, [
        clientPoints,
        isMapReady,
        mode,
        plotPotentialClient,
        potentialClient?.clientId,
        selectMapPoint,
    ]);

    const selectCaregiver = useCallback((point: WorkforceMapPoint) => {
        setFocusedCaregiver(point);
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        const constructors = maplibreConstructorsRef.current;
        if (!map || !constructors || !isMapReady || !focusedCaregiver) {
            return;
        }
        map.flyTo({
            center: [focusedCaregiver.longitude, focusedCaregiver.latitude],
            zoom: 12,
        });
        const label = document.createElement('div');
        label.textContent = formatName(focusedCaregiver.name);
        pointPopupRef.current?.remove();
        const popup = new constructors.Popup({ closeButton: false })
            .setLngLat([focusedCaregiver.longitude, focusedCaregiver.latitude])
            .setDOMContent(label)
            .addTo(map);
        pointPopupRef.current = popup;
        return () => {
            popup.remove();
        };
    }, [focusedCaregiver, formatName, isMapReady, visibleLocationsKey]);

    const clearSearch = () => {
        closeMapPoint();
        setPotentialClient(null);
        setFocusedCaregiver(null);
        setSearchClearEpoch((value) => value + 1);
        changeCaregiverSearch('');
    };
    const searchControl =
        mode === 'live' ? (
            <CoverageMapSearch
                key={`${organizationId}:${mode}`}
                organizationId={organizationId}
                caregiverSearch={caregiverSearch}
                selectedLocation={potentialClient}
                clientsLayerEnabled={clientsLayerEnabled}
                clientPoints={permittedClientPoints}
                caregiverPoints={points}
                isLoadingClientLocations={
                    clientsLayerEnabled && clients.isLoading
                }
                clientLocationsFailed={!!clients.error}
                onRetryClientLocations={clients.retry}
                openShiftClients={
                    clientsVisible && onlyOpenShiftClients
                        ? openShiftClients
                        : undefined
                }
                onPlotted={plotPotentialClient}
                onSelectCaregiver={(point) => {
                    setPotentialClient(null);
                    changeCaregiverSearch(point.name);
                    selectCaregiver(point);
                }}
                onClear={clearSearch}
                clearEpoch={searchClearEpoch + clearEpoch}
            />
        ) : undefined;
    const mapControls = clientsLayerEnabled ? (
        <Fragment>
            <label className="text-muted-foreground flex cursor-pointer items-center gap-1.5 text-sm">
                <Checkbox
                    checked={showClients}
                    onCheckedChange={(checked) =>
                        setShowClients(checked === true)
                    }
                    aria-label="Include clients"
                />
                Include clients
            </label>
            <label className="text-muted-foreground flex cursor-pointer items-center gap-1.5 text-sm">
                <Checkbox
                    checked={onlyOpenShiftClients}
                    onCheckedChange={(checked) => {
                        setOnlyOpenShiftClients(checked === true);
                        clearSearch();
                    }}
                />
                Only clients with open shifts
            </label>
        </Fragment>
    ) : null;
    const toolbar = renderToolbar ? (
        renderToolbar(searchControl, mapControls, () => {
            onRetry();
            clients.retry();
            if (clientsVisible) {
                openShiftClients.retry();
            }
            void shiftAvailability.mutate();
        })
    ) : (
        <div className="flex flex-wrap items-center gap-3">
            {searchControl}
            {mapControls}
        </div>
    );

    if (status === 'idle') {
        return toolbar ?? null;
    }
    if (status === 'loading') {
        return (
            <div
                className="flex flex-1 flex-col gap-3"
                aria-label="Loading caregiver locations"
            >
                {toolbar}
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="min-h-[360px] w-full flex-1 rounded-xl" />
            </div>
        );
    }
    if (status === 'error') {
        return (
            <Fragment>
                {toolbar}
                <div className="py-10 text-center">
                    <p className="text-muted-foreground text-sm">
                        {errorMessage ?? 'Couldn’t load the map.'}
                    </p>
                    <Button
                        variant="outline"
                        className="mt-3"
                        onClick={onRetry}
                    >
                        Try again
                    </Button>
                </div>
            </Fragment>
        );
    }

    return (
        <div className="flex flex-1 flex-col gap-3">
            {toolbar}
            {refreshErrorMessage ? (
                <div
                    className="border-border bg-muted/50 flex items-center gap-2 rounded-lg border px-3 py-2"
                    role="alert"
                >
                    <p className="text-muted-foreground grow text-sm">
                        {refreshErrorMessage}
                    </p>
                    <Button variant="ghost" size="sm" onClick={onRetry}>
                        Try again
                    </Button>
                </div>
            ) : null}
            {clientsVisible && clients.isLoading ? (
                <p className="text-muted-foreground text-xs">
                    Loading clients…
                </p>
            ) : null}
            {clientsVisible && clients.error ? (
                <div role="alert" className="text-muted-foreground text-xs">
                    Couldn’t load clients.{' '}
                    <Button size="sm" variant="ghost" onClick={clients.retry}>
                        Retry clients
                    </Button>
                </div>
            ) : null}
            {clientsVisible &&
            (onlyOpenShiftClients ||
                openShiftClients.isLoading ||
                openShiftClients.error) ? (
                <div
                    className="text-muted-foreground text-xs"
                    role={openShiftClients.error ? 'alert' : 'status'}
                >
                    {openShiftClients.error ? (
                        <Fragment>
                            Couldn’t load clients with open shifts.
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={openShiftClients.retry}
                            >
                                Retry open shifts
                            </Button>
                        </Fragment>
                    ) : openShiftClients.isLoading ? (
                        'Loading clients with open shifts…'
                    ) : (
                        `${clientPoints.length} mapped ${clientPoints.length === 1 ? 'client' : 'clients'} with open shifts in the next 30 days.`
                    )}
                </div>
            ) : null}
            {hasRendererError ? (
                <div className="py-10 text-center">
                    <p className="text-muted-foreground text-sm">
                        We couldn’t render the map. Check your connection and
                        try again.
                    </p>
                    <Button
                        variant="outline"
                        className="mt-3"
                        onClick={() => {
                            setHasRendererError(false);
                            setRendererAttempt((value) => value + 1);
                        }}
                    >
                        Try again
                    </Button>
                </div>
            ) : (
                <div
                    role="region"
                    aria-label="Coverage map"
                    className={
                        isMapFullscreen
                            ? 'bg-background fixed inset-0 z-50'
                            : 'relative min-h-[360px] flex-1'
                    }
                >
                    {!isMapReady ? (
                        <Skeleton className="absolute inset-0 z-10 rounded-xl" />
                    ) : null}
                    {isMapReady && visiblePoints.length === 0 ? (
                        <p className="bg-background/80 text-muted-foreground absolute inset-x-0 top-4 z-10 mx-auto w-fit rounded-md px-3 py-1.5 text-sm">
                            No mapped caregivers match the current filters.
                        </p>
                    ) : null}
                    {isMapReady && mode === 'live' && potentialClient ? (
                        <MapSelectionCard
                            organizationId={organizationId}
                            nearbyCaregivers={nearby}
                            radiusMiles={radiusMiles}
                            onRadiusChange={setRadiusMiles}
                            selectedShifts={selectedShifts}
                            onSelectedShiftsChange={setSelectedShifts}
                            availability={shiftAvailability}
                            onSelectedCaregiverIdsChange={
                                updateOutreachSelection
                            }
                            colorByCaregiver={colorByCaregiver}
                            plotted={potentialClient}
                            onClear={clearSearch}
                            onSelectCaregiver={selectCaregiver}
                        />
                    ) : null}
                    <div
                        ref={containerRef}
                        className="absolute inset-0 h-full w-full rounded-xl border"
                        role="application"
                        aria-label="Map of caregiver and client locations"
                    />
                </div>
            )}
            {selectedPoint &&
            organizationId &&
            isMapReady &&
            mapRef.current &&
            maplibreConstructorsRef.current ? (
                <MapPointPopup
                    key={`${selectedPoint.kind}:${selectedPoint.id}`}
                    map={mapRef.current}
                    PopupConstructor={maplibreConstructorsRef.current.Popup}
                    point={selectedPoint}
                    organizationId={organizationId}
                    onClose={closeMapPoint}
                />
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
                {hasSelectedShifts ? (
                    <span className="text-sm font-medium">
                        {activeShiftIds.length === 1
                            ? 'Availability for selected shift'
                            : 'Availability for selected shifts'}
                    </span>
                ) : null}
                <p className="text-muted-foreground text-xs">
                    {hasSelectedShifts
                        ? shiftAvailability.error
                            ? 'Couldn’t check shift availability. Retry in the caregiver list.'
                            : shiftAvailability.isLoading ||
                                shiftAvailability.isValidating
                              ? 'Checking availability…'
                              : 'Available means available for the full duration of every selected shift.'
                        : 'Showing who’s available right now.'}
                </p>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    <span
                        className="bg-foreground size-2.5 rounded-full"
                        aria-hidden="true"
                    />
                    Caregiver
                </span>
                {hasSelectedShifts ? (
                    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <span
                            className="size-2.5 rounded-full"
                            style={{ backgroundColor: CLIENT_MARKER_COLOR }}
                            aria-hidden="true"
                        />
                        Selected for outreach
                    </span>
                ) : null}
                {clientsVisible ? (
                    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <Star
                            className="size-3.5 fill-current"
                            style={{ color: CLIENT_MARKER_COLOR }}
                            aria-hidden="true"
                        />
                        Client
                    </span>
                ) : null}
                {clientsVisible ? (
                    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <Star
                            className="size-3.5 fill-current"
                            style={{ color: OPEN_SHIFT_CLIENT_MARKER_COLOR }}
                            aria-hidden="true"
                        />
                        Client with open shifts (next 30 days)
                    </span>
                ) : null}
                {potentialClient && !potentialClient.clientId ? (
                    <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                        <Star
                            className="size-3.5 fill-current"
                            style={{ color: CLIENT_MARKER_COLOR }}
                            aria-hidden="true"
                        />
                        Searched address (temporary)
                    </span>
                ) : null}
                <span className="bg-border h-3.5 w-px" aria-hidden="true" />
                {Object.entries(availabilityStyles).map(([state, style]) => (
                    <StatusBadge key={state} status={style} variant="outline" />
                ))}
            </div>
        </div>
    );
}
